/**
 * Manages print-related services and viewport settings.
 *
 * @param {function} handleOnChangePrintMode - A callback function to handle changes in the print mode.
 * @returns {void}
 */
const managerService = (handleOnChangePrintMode) => {
    /**
     * Checks if the 'window' object and 'matchMedia' function are available, and if so, creates a MediaQueryList
     * object for 'print' media type.
     *
     * @returns {(MediaQueryList|null)} MediaQueryList object if both 'window' and 'matchMedia' are defined, null otherwise.
     */
    const printMq =
        typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('print')

    /**
     * Stores the original window.print function reference.
     * This allows restoring it after replacing with a custom print function.
     */
    const originalWindowPrint = window.print

    window.print = () => {
        runPrintService()
    }

    /**
     * Smoothly scrolls the window to a specified vertical position.
     *
     * @param {number} top - The vertical position to scroll to.
     */
    const windowScrollTo = (top) =>
        window.scrollTo({
            top: top,
            left: 0,
            behavior: 'smooth'
        })

    /**
     * Scrolls through all elements marked with the data-id "google-map" on the page, in sequence, with a timed interval.
     *
     * Returns a promise that resolves once all elements have been scrolled through and the page is reset to the top.
     *
     * @returns {Promise<boolean>} A promise that resolves to true once scrolling and reset are complete.
     */
    const scrollToAllGoogleMaps = () => {
        return new Promise((resolve) => {
            const mapElements = [
                ...document.querySelectorAll('[data-id="google-map"]')
            ]
            const timeInterval = 300

            //
            mapElements.forEach((element, index) => {
                setTimeout(() => {
                    windowScrollTo(
                        element.getBoundingClientRect().top + window.scrollY
                    )
                }, index * timeInterval)
            })

            //
            setTimeout(() => {
                windowScrollTo(0)
                resolve(true)
            }, timeInterval)
        })
    }

    /**
     * Sets the default viewport meta tag for responsive design, if not already set.
     *
     * @returns {boolean} Returns true if the viewport was updated, false if it was already set correctly.
     */
    const setDefaultViewPort = () => {
        const viewport = document.getElementsByName('viewport')[0]

        if (
            viewport.getAttribute('content') ==
            'width=device-width, initial-scale=1.0'
        ) {
            return false
        }

        viewport.setAttribute(
            'content',
            'width=device-width, initial-scale=1.0'
        )

        return true
    }

    /**
     * Sets the viewport content attribute for print view, if not already set, to a specific width and scale.
     *
     * @returns {boolean} Returns true if the viewport content was updated, false if it was already set to the desired values.
     */
    const setPrintViewPort = () => {
        const viewport = document.getElementsByName('viewport')[0]

        if (
            viewport.getAttribute('content') ==
            'width=1200, initial-scale=1.0, maximum-scale=1.0'
        ) {
            return false
        }

        viewport.setAttribute(
            'content',
            'width=1200, initial-scale=1.0, maximum-scale=1.0'
        )

        return true
    }

    /**
     * Handles the 'keydown' event to intercept 'Ctrl + P' or 'Cmd + P' key combination for printing.
     *
     * @param {KeyboardEvent} event - The keyboard event object containing details of the pressed keys.
     */
    const keydown = (event) => {
        // Check if 'P' was pressed along with the Control key or Command key
        if (event.key === 'p' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault() // Prevent the default print dialog

            runPrintService()
        }
    }

    /**
     * Initiates the print service by setting the viewport for print, adding print mode class, and handling print mode change.
     * After the initial setup, it scrolls to all Google Maps and prints the document with a delay.
     */
    const runPrintService = () => {
        initiatePrint()
            .then(() => scrollToAllGoogleMaps())
            .then(() => {
                setTimeout(() => {
                    originalWindowPrint()
                }, 200)
            })
    }

    /**
     * Initiates the print process by setting up the necessary view port and adding the "print" class to the HTML element.
     * Resolves a promise after a short delay to ensure that the setup is complete before proceeding.
     *
     * @returns {Promise<boolean>} A promise that resolves to true, indicating that the print setup is complete.
     */
    const initiatePrint = () =>
        new Promise((resolve) => {
            //
            setPrintViewPort()

            //
            document.querySelector('html').classList.add('print')

            //
            handleOnChangePrintMode(true)

            //
            setTimeout(() => {
                resolve(true)
            }, 100)
        })

    /**
     * Initiates the print process by first calling the initiatePrint function and then scrolling to all Google Maps elements.
     *
     * @returns {Promise<void>} A promise that resolves when both the printing initiation and scrolling actions are complete.
     */
    const beforePrint = () => {
        initiatePrint().then(() => scrollToAllGoogleMaps())
    }

    //
    const afterPrint = () => {
        //
        if (document.querySelector('html').classList.contains('print')) {
            document.querySelector('html').classList.remove('print')
        }

        //
        handleOnChangePrintMode(printMq.matches)

        //
        setDefaultViewPort()
    }

    /**
     * Initializes detection of media query for print and invokes beforePrint if conditions are met.
     *
     * @returns {void} No return value.
     */
    const onInitDetectMediaAndPrint = () => {
        if (printMq.matches) {
            beforePrint()
        }
    }

    document.addEventListener('keydown', keydown)

    /**
     * Restores the original window.print function and removes the keydown event listener.
     *
     * @returns {void} No return value.
     */
    const onDeconstruct = () => {
        window.print = originalWindowPrint
        document.removeEventListener('keydown', keydown)
    }

    //
    onInitDetectMediaAndPrint()

    //
    return {
        beforePrint,
        afterPrint,
        onDeconstruct
    }
}

export default managerService
